import React, { useCallback, useEffect, useRef, useState } from 'react';
import axios from 'axios';
import { Plus } from 'lucide-react';
import { useSortableData, sortIndicator } from '../hooks/useSortableData';
import { useDivisionContext } from '../context/DivisionContext';
import { useAuth } from '../context/AuthContext';
import { useToast } from '../context/ToastContext';
import Card from '../components/Card';
import PageHeader from '../components/PageHeader';

const Seasons = () => {
  const { isAdmin, isSystemAdmin } = useAuth();
  const { refreshSeasons, setSelectedSeasonId: selectSeason } = useDivisionContext();
  const toast = useToast();

  const dateInputToUtcIso = (value, options = {}) => {
    if (!value) return null;
    const { endOfDay = false } = options;
    const [y, m, d] = String(value).split('-').map((x) => Number(x));
    if (!y || !m || !d) return null;
    if (endOfDay) {
      return new Date(Date.UTC(y, m - 1, d, 23, 59, 59, 999)).toISOString();
    }
    return new Date(Date.UTC(y, m - 1, d, 0, 0, 0, 0)).toISOString();
  };

  const [seasons, setSeasons] = useState([]);
  const [activeSeason, setActiveSeason] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showSeasonForm, setShowSeasonForm] = useState(false);
  const [creatingName, setCreatingName] = useState('');
  const [creatingScheduleStart, setCreatingScheduleStart] = useState('');
  const [creatingScheduleEnd, setCreatingScheduleEnd] = useState('');
  const [copyFromSeasonId, setCopyFromSeasonId] = useState('');
  const [generating, setGenerating] = useState(false);
  const [fixtureCounts, setFixtureCounts] = useState({});
  const didInitRef = useRef(false);

  const [managingSeasonId, setManagingSeasonId] = useState('');
  const [divisionCreatingName, setDivisionCreatingName] = useState('');
  const [divisions, setDivisions] = useState([]);
  const [teams, setTeams] = useState([]);
  const [divisionTeamSelections, setDivisionTeamSelections] = useState({});
  const [divisionsLoading, setDivisionsLoading] = useState(false);
  const [divisionSaving, setDivisionSaving] = useState(false);
  const [divisionDirty, setDivisionDirty] = useState(false);
  const [fixtureInfoSeasonId, setFixtureInfoSeasonId] = useState('');
  const [fixturePreviewSeasonId, setFixturePreviewSeasonId] = useState('');
  const [fixturePreviewLoading, setFixturePreviewLoading] = useState(false);
  const [fixturePreview, setFixturePreview] = useState(null);
  const [fixtureMultipliers, setFixtureMultipliers] = useState({});
  const [blockedDatesSeasonId, setBlockedDatesSeasonId] = useState('');
  const [blockedDateInput, setBlockedDateInput] = useState('');
  const [blockedNameInput, setBlockedNameInput] = useState('');
  const [blockedDatesDraft, setBlockedDatesDraft] = useState([]);
  const [savingBlocked, setSavingBlocked] = useState(false);
  const [cupDrawSeasonId, setCupDrawSeasonId] = useState('');
  const [cupDrawLoading, setCupDrawLoading] = useState(false);
  const [cupDrawSaving, setCupDrawSaving] = useState('');
  const [cupDrawDivisions, setCupDrawDivisions] = useState([]);
  const [cupDrawSelections, setCupDrawSelections] = useState({});

  const fetchData = useCallback(async () => {
    try {
      const [all, active] = await Promise.all([
        axios.get('/api/team-seasons'),
        axios.get('/api/team-seasons/active'),
      ]);
      setSeasons(all.data);
      setActiveSeason(active.data);

      const countsRes = await axios.get('/api/fixtures/counts-by-season');
      setFixtureCounts(countsRes.data || {});
    } catch (e) {
      console.error(e);
      toast.error(e?.response?.data?.error || e.message);
    } finally {
      setLoading(false);
    }
  }, [toast]);

  useEffect(() => {
    if (didInitRef.current) return;
    didInitRef.current = true;
    fetchData();
  }, [fetchData]);

  const loadFixturePreview = async (seasonId, multipliers) => {
    setFixturePreviewLoading(true);
    try {
      const res = await axios.post('/api/fixtures/generate-schedule/preview', {
        team_season_id: seasonId,
        fixture_multipliers: multipliers,
      });
      setFixturePreview(res.data || null);
    } catch (e) {
      console.error(e);
      toast.error(e?.response?.data?.error || e.message);
      setFixturePreviewSeasonId('');
    } finally {
      setFixturePreviewLoading(false);
    }
  };

  const openFixturePreview = async (seasonId) => {
    if (!seasonId) return;
    if (!isAdmin) return;
    setFixturePreviewSeasonId(seasonId);
    setFixtureMultipliers({});
    setFixturePreview(null);
    await loadFixturePreview(seasonId, {});
  };

  const setDivisionMultiplier = (divisionId, value) => {
    const next = { ...fixtureMultipliers, [divisionId]: value };
    setFixtureMultipliers(next);
    loadFixturePreview(fixturePreviewSeasonId, next);
  };

  const closeFixturePreview = () => {
    setFixturePreviewSeasonId('');
    setFixturePreviewLoading(false);
    setFixturePreview(null);
  };

  const openBlockedDates = (season) => {
    if (!season) return;
    setBlockedDatesSeasonId(season.id);
    setBlockedDatesDraft(
      (season.blocked_dates || []).map((d) => ({
        date: d && typeof d === 'object' ? d.date : d,
        name: d && typeof d === 'object' ? d.name || '' : '',
      }))
    );
    setBlockedDateInput('');
    setBlockedNameInput('');
  };

  const closeBlockedDates = () => {
    setBlockedDatesSeasonId('');
    setBlockedDatesDraft([]);
    setBlockedDateInput('');
    setBlockedNameInput('');
  };

  const addBlockedDate = (e) => {
    e.preventDefault();
    if (!blockedDateInput) return;
    const iso = dateInputToUtcIso(blockedDateInput);
    if (!iso) return;
    if (blockedDatesDraft.some((d) => d.date === iso)) return;
    const next = [...blockedDatesDraft, { date: iso, name: blockedNameInput.trim() }].sort((a, b) => a.date.localeCompare(b.date));
    setBlockedDatesDraft(next);
    setBlockedDateInput('');
    setBlockedNameInput('');
  };

  const updateBlockedDateName = (iso, name) => {
    setBlockedDatesDraft(blockedDatesDraft.map((d) => (d.date === iso ? { ...d, name } : d)));
  };

  const removeBlockedDate = (iso) => {
    setBlockedDatesDraft(blockedDatesDraft.filter((d) => d.date !== iso));
  };

  const saveBlockedDates = async () => {
    if (!isAdmin || !blockedDatesSeasonId) return;
    setSavingBlocked(true);
    try {
      await axios.put(`/api/team-seasons/${blockedDatesSeasonId}/blocked-dates`, {
        blocked_dates: blockedDatesDraft,
      });
      toast.success('Blocked dates updated');
      await fetchData();
      closeBlockedDates();
    } catch (e) {
      console.error(e);
      toast.error(e?.response?.data?.error || e.message);
    } finally {
      setSavingBlocked(false);
    }
  };

  const openCupDraw = async (seasonId) => {
    setCupDrawSeasonId(seasonId);
    setCupDrawLoading(true);
    setCupDrawDivisions([]);
    setCupDrawSelections({});
    try {
      const res = await axios.get(`/api/team-seasons/${seasonId}/cup-draw`);
      const divs = res.data?.divisions || [];
      setCupDrawDivisions(divs);
      const selections = {};
      for (const d of divs) {
        const slots = Array.from({ length: d.first_round_slots }, () => ({ home: '', away: '' }));
        for (const m of d.draw || []) {
          const slot = slots[m.match_number - 1];
          if (slot) {
            slot.home = m.home_team_id || '';
            slot.away = m.away_team_id || '';
          }
        }
        selections[d.division_id] = slots;
      }
      setCupDrawSelections(selections);
    } catch (e) {
      toast.error(e?.response?.data?.error || 'Failed to load cup draw');
      setCupDrawSeasonId('');
    } finally {
      setCupDrawLoading(false);
    }
  };

  const closeCupDraw = () => {
    setCupDrawSeasonId('');
    setCupDrawDivisions([]);
    setCupDrawSelections({});
    setCupDrawSaving('');
  };

  // Picking a team removes it from whatever other slot held it, so a team
  // can never appear twice in a division's draw.
  const setCupSlot = (divisionId, slotIdx, side, teamId) => {
    setCupDrawSelections((prev) => ({
      ...prev,
      [divisionId]: (prev[divisionId] || []).map((slot, i) => {
        const next = { ...slot };
        if (teamId) {
          if (next.home === teamId) next.home = '';
          if (next.away === teamId) next.away = '';
        }
        if (i === slotIdx) next[side] = teamId;
        return next;
      }),
    }));
  };

  const randomizeCupDraw = (division) => {
    const ids = (division.teams || []).map((t) => t.id);
    for (let i = ids.length - 1; i > 0; i--) {
      const j = Math.floor(Math.random() * (i + 1));
      [ids[i], ids[j]] = [ids[j], ids[i]];
    }
    const slots = Array.from({ length: division.first_round_slots }, () => ({ home: '', away: '' }));
    const byeCount = division.first_round_slots * 2 - ids.length;
    const byeSlots = new Set();
    while (byeSlots.size < byeCount) {
      byeSlots.add(Math.floor(Math.random() * slots.length));
    }
    let idx = 0;
    slots.forEach((slot, i) => {
      if (byeSlots.has(i)) {
        slot.home = ids[idx++] || '';
      } else {
        const a = ids[idx++];
        const b = ids[idx++];
        if (Math.random() < 0.5) {
          slot.home = a || '';
          slot.away = b || '';
        } else {
          slot.home = b || '';
          slot.away = a || '';
        }
      }
    });
    setCupDrawSelections((prev) => ({ ...prev, [division.division_id]: slots }));
  };

  const saveCupDraw = async (division, matches) => {
    setCupDrawSaving(division.division_id);
    try {
      await axios.put(`/api/team-seasons/${cupDrawSeasonId}/cup-draw`, {
        division_id: division.division_id,
        matches,
      });
      toast.success(
        matches.length
          ? `Cup draw saved for ${division.division_name}`
          : `Cup draw cleared for ${division.division_name}`
      );
      await openCupDraw(cupDrawSeasonId);
    } catch (e) {
      toast.error(e?.response?.data?.error || 'Failed to save cup draw');
    } finally {
      setCupDrawSaving('');
    }
  };

  const submitCupDraw = (division) => {
    const slots = cupDrawSelections[division.division_id] || [];
    const used = new Set();
    for (const s of slots) {
      if (!s.home && !s.away) {
        toast.error('Each cup match needs at least one team (or a bye)');
        return;
      }
      for (const t of [s.home, s.away]) {
        if (!t) continue;
        if (used.has(t)) {
          toast.error('Each team can only appear once in the cup draw');
          return;
        }
        used.add(t);
      }
    }
    if (used.size !== (division.teams || []).length) {
      toast.error('Every division team must appear in the cup draw');
      return;
    }
    void saveCupDraw(division, slots.map((s) => ({
      home_team_id: s.home || null,
      away_team_id: s.away || null,
    })));
  };

  const openDivisions = async (seasonId) => {
    setManagingSeasonId(seasonId);
    setDivisionsLoading(true);
    try {
      const [divRes, teamRes] = await Promise.all([
        axios.get(`/api/team-seasons/${seasonId}/divisions`),
        axios.get('/api/teams'),
      ]);

      setDivisions(divRes.data || []);
      setTeams(teamRes.data || []);

      const activeTeamIds = new Set((teamRes.data || []).map((x) => x.id));

      const divs = divRes.data || [];
      const entries = await Promise.all(
        divs.map(async (d) => {
          const t = await axios.get(`/api/divisions/${d.id}/teams`);
          return [d.id, new Set((t.data || []).map((x) => x.id).filter((id) => activeTeamIds.has(id)))];
        })
      );
      setDivisionTeamSelections(Object.fromEntries(entries));
    } catch (e) {
      console.error(e);
      toast.error(e?.response?.data?.error || e.message);
    } finally {
      setDivisionsLoading(false);
    }
  };

  const closeDivisions = () => {
    setManagingSeasonId('');
    setDivisionCreatingName('');
    setDivisions([]);
    setTeams([]);
    setDivisionTeamSelections({});
    setDivisionSaving(false);
    setDivisionDirty(false);
  };

  const createDivision = async (e) => {
    e.preventDefault();
    if (!isAdmin) return;
    if (!managingSeasonId) return;
    try {
      await axios.post(`/api/team-seasons/${managingSeasonId}/divisions`, { name: divisionCreatingName });
      setDivisionCreatingName('');
      await openDivisions(managingSeasonId);
    } catch (e2) {
      console.error(e2);
      toast.error(e2?.response?.data?.error || e2.message);
    }
  };

  const toggleTeamInDivision = (divisionId, teamId) => {
    setDivisionTeamSelections((prev) => {
      const next = { ...prev };
      const current = next[divisionId] ? new Set(Array.from(next[divisionId])) : new Set();
      if (current.has(teamId)) current.delete(teamId);
      else current.add(teamId);
      next[divisionId] = current;
      return next;
    });
    setDivisionDirty(true);
  };

  const saveAllDivisionTeams = async () => {
    if (!isAdmin) return;
    if (!managingSeasonId) return;
    if (isManagingLocked) return;
    setDivisionSaving(true);
    try {
      for (const d of divisions) {
        const ids = Array.from(divisionTeamSelections[d.id] || []);
        await axios.put(`/api/divisions/${d.id}/teams`, { teamIds: ids });
      }
      toast.success('Save successful');
      await openDivisions(managingSeasonId);
      setDivisionDirty(false);
    } catch (e) {
      console.error(e);
      toast.error(e?.response?.data?.error || e.message);
    } finally {
      setDivisionSaving(false);
    }
  };

  const deleteDivision = async (division) => {
    if (!isAdmin) return;
    if (!division?.id) return;
    if (managingSeason?.status !== 'draft') return;
    if (!window.confirm(`Remove division "${division.name}"? This cannot be undone.`)) return;
    try {
      await axios.delete(`/api/divisions/${division.id}`);
      toast.success('Division removed');
      await openDivisions(managingSeasonId);
    } catch (e) {
      console.error(e);
      toast.error(e?.response?.data?.error || e.message);
    }
  };

  const createSeason = async (e) => {
    e.preventDefault();
    if (!isSystemAdmin) return;
    try {
      if (!creatingScheduleStart || !creatingScheduleEnd) {
        toast.error('Start and end dates are required');
        return;
      }

      const startIso = dateInputToUtcIso(creatingScheduleStart);
      const endIso = dateInputToUtcIso(creatingScheduleEnd, { endOfDay: true });
      if (!startIso || !endIso) {
        toast.error('Invalid start or end date');
        return;
      }

      const created = await axios.post('/api/team-seasons', {
        name: creatingName,
        copyFromSeasonId: copyFromSeasonId || undefined,
        schedule_start_date: startIso,
        schedule_end_date: endIso,
      });
      setCreatingName('');
      setCreatingScheduleStart('');
      setCreatingScheduleEnd('');
      setCopyFromSeasonId('');
      setShowSeasonForm(false);
      await fetchData();
      await refreshSeasons();
      if (created?.data?.id) {
        await selectSeason(created.data.id);
      }
      toast.success('Save successful');
    } catch (e) {
      console.error(e);
      toast.error(e?.response?.data?.error || e.message);
    }
  };

  const startSeason = async (id) => {
    if (!isSystemAdmin) return;
    try {
      await axios.post(`/api/team-seasons/${id}/start`, {});
      await fetchData();
      await refreshSeasons();
      await selectSeason(id);
      toast.success('Save successful');
    } catch (e) {
      console.error(e);
      toast.error(e?.response?.data?.error || e.message);
    }
  };

  const reopenSeason = async (id) => {
    if (!isSystemAdmin) return;
    try {
      await axios.post(`/api/team-seasons/${id}/reopen`, {});
      await fetchData();
      await refreshSeasons();
      toast.success('Save successful');
    } catch (e) {
      console.error(e);
      toast.error(e?.response?.data?.error || e.message);
    }
  };

  const revertToDraft = async (id) => {
    if (!isSystemAdmin) return;
    if (!window.confirm(
      'Revert this season to draft? This will DELETE all generated fixtures so divisions and cup draws can be edited. Saved cup draws are kept.'
    )) return;
    try {
      await axios.post(`/api/team-seasons/${id}/revert-to-draft`, {});
      await fetchData();
      await refreshSeasons();
      toast.success('Season reverted to draft');
    } catch (e) {
      console.error(e);
      toast.error(e?.response?.data?.error || e.message);
    }
  };

  const finishSeason = async (id) => {
    if (!isSystemAdmin) return;
    try {
      await axios.post(`/api/team-seasons/${id}/stop`, {});
      await fetchData();
      await refreshSeasons();
      toast.success('Save successful');
    } catch (e) {
      console.error(e);
      toast.error(e?.response?.data?.error || e.message);
    }
  };

  const deleteSeason = async (id) => {
    if (!isSystemAdmin) return;
    if (!window.confirm('Delete this season? This cannot be undone.')) return;
    try {
      await axios.delete(`/api/team-seasons/${id}`);
      await fetchData();
      await refreshSeasons();
      toast.success('Delete successful');
    } catch (e) {
      console.error(e);
      toast.error(e?.response?.data?.error || e.message);
    }
  };

  const generateFixtures = async (seasonId) => {
    if (!seasonId) return;
    if (!isAdmin) return;
    setGenerating(true);
    try {
      const res = await axios.post('/api/fixtures/generate-schedule', {
        team_season_id: seasonId,
        fixture_multipliers: fixtureMultipliers,
      });
      const created = Array.isArray(res.data) ? res.data : [];
      const unscheduled = created.filter((f) => !f.match_date);
      if (unscheduled.length > 0) {
        const names = unscheduled
          .slice(0, 5)
          .map((f) => `${f.home_team_name || 'Home'} vs ${f.away_team_name || 'Away'}`)
          .join(', ');
        toast.warning(
          `Generated, but ${unscheduled.length} fixture(s) could not be placed on the home team's preferred days: ${names}` +
          `${unscheduled.length > 5 ? ` (+${unscheduled.length - 5} more)` : ''}. They are listed under Unscheduled fixtures.`
        );
      } else {
        toast.success('Fixtures generated');
      }
      await fetchData();
    } catch (e) {
      console.error(e);
      toast.error(e?.response?.data?.error || e.message);
    } finally {
      setGenerating(false);
    }
  };

  const badge = (status) => {
    const styles = {
      draft: 'bg-gray-100 text-gray-800',
      ready: 'bg-yellow-100 text-yellow-800',
      active: 'bg-green-100 text-green-800',
      concluded: 'bg-blue-100 text-blue-800',
    };
    return <span className={`px-2 py-1 text-xs rounded-full ${styles[status] || styles.draft}`}>{status}</span>;
  };

  const { items: sortedSeasons, requestSort, sortConfig } = useSortableData(seasons, {
    key: 'name',
    direction: 'asc',
  });

  if (loading) return <div className="text-center py-8">Loading seasons...</div>;

  const managingSeason = managingSeasonId ? seasons.find((x) => x.id === managingSeasonId) : null;
  const isManagingLocked = managingSeason ? !['draft', 'ready'].includes(managingSeason.status) : false;
  const isManagingDraft = managingSeason ? managingSeason.status === 'draft' : false;

  return (
    <div className="space-y-6">
      <PageHeader
        title="Seasons"
        right={
          <div className="flex items-center gap-3">
            <div className="text-sm text-gray-600">
              Active: <span className="font-medium">{activeSeason?.name || 'None'}</span>
            </div>
            {isSystemAdmin ? (
              <button
                type="button"
                className="btn btn-primary flex items-center gap-2"
                onClick={() => setShowSeasonForm(true)}
              >
                <Plus size={18} />
                Add Season
              </button>
            ) : null}
          </div>
        }
      />

      {isSystemAdmin && showSeasonForm && (
        <Card>
          <h3 className="text-lg font-semibold text-gray-800 mb-4">Add New Season</h3>
          <form onSubmit={createSeason} className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-5 gap-3">
              <input
                className="input"
                value={creatingName}
                onChange={(e) => setCreatingName(e.target.value)}
                placeholder="Season name"
              />
              <input
                className="input"
                type="date"
                value={creatingScheduleStart}
                onChange={(e) => setCreatingScheduleStart(e.target.value)}
              />
              <input
                className="input"
                type="date"
                value={creatingScheduleEnd}
                onChange={(e) => setCreatingScheduleEnd(e.target.value)}
              />
              <select className="input" value={copyFromSeasonId} onChange={(e) => setCopyFromSeasonId(e.target.value)}>
                <option value="">(Optional) Copy divisions + teams from season</option>
                {seasons
                  .filter((s) => s.id !== activeSeason?.id)
                  .map((s) => (
                    <option key={s.id} value={s.id}>
                      {s.name}
                    </option>
                  ))}
              </select>
            </div>
            <div className="flex flex-wrap gap-2">
              <button className="btn btn-success" type="submit">Create</button>
              <button
                className="btn btn-secondary"
                type="button"
                onClick={() => {
                  setCreatingName('');
                  setCreatingScheduleStart('');
                  setCreatingScheduleEnd('');
                  setCopyFromSeasonId('');
                  setShowSeasonForm(false);
                }}
              >
                Cancel
              </button>
            </div>
          </form>
        </Card>
      )}

      <Card>
        <h3 className="text-lg font-semibold text-gray-800 mb-4">Manage Seasons</h3>

        <div className="overflow-x-auto">
          <table className="table">
            <thead>
              <tr>
                <th className="cursor-pointer" onClick={() => requestSort('name')}>Name{sortIndicator(sortConfig, 'name')}</th>
                <th className="cursor-pointer" onClick={() => requestSort('status')}>Status{sortIndicator(sortConfig, 'status')}</th>
                <th className="cursor-pointer" onClick={() => requestSort('schedule_start_date')}>Start{sortIndicator(sortConfig, 'schedule_start_date')}</th>
                <th className="cursor-pointer" onClick={() => requestSort('schedule_end_date')}>End{sortIndicator(sortConfig, 'schedule_end_date')}</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {sortedSeasons.map((s) => (
                <tr key={s.id}>
                  <td className="font-medium">{s.name}</td>
                  <td>{badge(s.status)}</td>
                  <td>{s.schedule_start_date ? new Date(s.schedule_start_date).toLocaleDateString() : '-'}</td>
                  <td>{s.schedule_end_date ? new Date(s.schedule_end_date).toLocaleDateString() : '-'}</td>
                  <td>
                    <div className="flex gap-2">
                      <button className="btn btn-secondary" onClick={() => openDivisions(s.id)}>
                        Divisions
                      </button>
                      {isAdmin && (
                        <button className="btn btn-secondary" onClick={() => openBlockedDates(s)} disabled={savingBlocked}>
                          Blocked Dates{(s.blocked_dates || []).length > 0 ? ` (${s.blocked_dates.length})` : ''}
                        </button>
                      )}
                      {isAdmin && s.status === 'draft' && (
                        <button className="btn btn-secondary" onClick={() => openCupDraw(s.id)} disabled={cupDrawLoading}>
                          Cup Draw
                        </button>
                      )}
                      {isAdmin && ((s.status === 'draft' && (fixtureCounts[s.id] || 0) === 0) || s.status === 'ready') && (
                        <>
                          <button className="btn btn-warning" onClick={() => openFixturePreview(s.id)} disabled={generating}>
                            <span className="flex items-center gap-2">
                              <span>{generating ? 'Generating...' : s.status === 'ready' ? 'Regenerate Fixtures' : 'Generate Fixtures'}</span>
                              <span
                                role="button"
                                tabIndex={0}
                                className="inline-flex items-center justify-center h-6 w-6 rounded-full border border-yellow-300 bg-white text-yellow-800 hover:bg-yellow-50"
                                onClick={(e) => {
                                  e.preventDefault();
                                  e.stopPropagation();
                                  setFixtureInfoSeasonId(s.id);
                                }}
                                onKeyDown={(e) => {
                                  if (e.key === 'Enter' || e.key === ' ') {
                                    e.preventDefault();
                                    e.stopPropagation();
                                    setFixtureInfoSeasonId(s.id);
                                  }
                                }}
                                title="How fixtures are created"
                                aria-label="How fixtures are created"
                              >
                                i
                              </span>
                            </span>
                          </button>
                        </>
                      )}
                      {isSystemAdmin && s.status === 'ready' && (
                        <button className="btn btn-secondary" onClick={() => revertToDraft(s.id)}>
                          Back to Draft
                        </button>
                      )}
                      {isSystemAdmin && s.status === 'ready' && (
                        <button className="btn btn-success" onClick={() => startSeason(s.id)}>
                          Start
                        </button>
                      )}
                      {isSystemAdmin && s.status === 'active' && (
                        <button className="btn btn-warning" onClick={() => finishSeason(s.id)}>
                          Close
                        </button>
                      )}

                      {isSystemAdmin && s.status === 'concluded' && (() => {
                        const blocked = activeSeason && activeSeason.id !== s.id;
                        const reason = blocked ? 'Cannot reopen while another season is in progress' : 'Reopen this season';
                        return (
                          <button
                            className="btn btn-secondary"
                            onClick={() => reopenSeason(s.id)}
                            disabled={!!blocked}
                            title={reason}
                          >
                            Reopen
                          </button>
                        );
                      })()}

                      {isSystemAdmin && s.status !== 'active' && (
                        <button className="btn btn-danger" onClick={() => deleteSeason(s.id)}>
                          Delete
                        </button>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          {seasons.length === 0 && <div className="text-gray-500 text-center py-6">No seasons yet</div>}
        </div>
      </Card>

      {managingSeasonId && (
        <Card>
          <div className="flex justify-between items-center mb-4">
            <h3 className="text-lg font-semibold text-gray-800">
              Divisions: <span className="font-medium">{managingSeason?.name || ''}</span>
            </h3>
            <div className="flex gap-2">
              {isAdmin && !isManagingLocked && (
                <button
                  className="btn btn-success"
                  onClick={saveAllDivisionTeams}
                  disabled={divisionsLoading || divisionSaving || !divisionDirty}
                >
                  {divisionSaving ? 'Saving...' : 'Save Changes'}
                </button>
              )}
              <button className="btn btn-secondary" onClick={closeDivisions}>Close</button>
            </div>
          </div>

          {isAdmin && (
            <form onSubmit={createDivision} className="flex gap-3 mb-4">
            <input
              className="input flex-1"
              value={divisionCreatingName}
              onChange={(e) => setDivisionCreatingName(e.target.value)}
              placeholder="New division name (e.g. Premier, Division 1A)"
              disabled={isManagingLocked}
            />
            <button className="btn btn-success" type="submit" disabled={isManagingLocked || divisionsLoading || !divisionCreatingName.trim()}>
              Create Division
            </button>
            </form>
          )}

          {divisionsLoading ? (
            <div className="text-center py-6 text-gray-600">Loading divisions...</div>
          ) : (
            <div className="space-y-6">
              {divisions.map((d) => (
                <div key={d.id} className="border rounded-lg p-4">
                  <div className="flex justify-between items-center mb-3">
                    <div className="font-medium text-gray-800">{d.name}</div>
                    {isAdmin && isManagingDraft && (
                      <button className="btn btn-danger" type="button" onClick={() => deleteDivision(d)}>
                        Remove
                      </button>
                    )}
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-2">
                    {teams.map((t) => {
                      const checked = !!divisionTeamSelections[d.id]?.has(t.id);
                      const takenElsewhere = Object.entries(divisionTeamSelections).some(
                        ([otherDivisionId, set]) => otherDivisionId !== d.id && set?.has(t.id)
                      );
                      const disabled = isManagingLocked || (!checked && takenElsewhere);
                      return (
                        <label key={t.id} className="flex items-center gap-2 text-sm text-gray-700">
                          <input
                            type="checkbox"
                            checked={checked}
                            disabled={!isAdmin || disabled}
                            onChange={() => toggleTeamInDivision(d.id, t.id)}
                          />
                          <span>{t.name}</span>
                        </label>
                      );
                    })}
                  </div>

                  {teams.length === 0 && (
                    <div className="text-sm text-gray-500">No teams available.</div>
                  )}
                </div>
              ))}

              {divisions.length === 0 && (
                <div className="text-gray-500 text-center py-6">No divisions yet</div>
              )}
            </div>
          )}

          <div className="text-sm text-gray-500 mt-4">
            {isManagingLocked
              ? 'This season is active or concluded. Divisions and team composition are read-only.'
              : 'Each team should be in only one division per season.'}
          </div>
        </Card>
      )}

      {fixtureInfoSeasonId && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/30 px-4">
          <div className="w-full max-w-xl rounded-lg bg-white shadow-lg border p-5">
            <div className="flex items-start justify-between gap-4 mb-3">
              <div>
                <div className="text-lg font-semibold text-gray-800">How fixtures are created</div>
              </div>
              <button className="btn btn-secondary" type="button" onClick={() => setFixtureInfoSeasonId('')}>
                Close
              </button>
            </div>

            <div className="space-y-3 text-sm text-gray-700">
              <div>
                It creates a full home-and-away league schedule for each division (every team plays every other team twice).
              </div>
              <div>
                It also creates cup fixtures from each division&apos;s cup draw (knockout bracket) — configured via the
                Cup Draw button while the season is in draft; a random draw is used for divisions with no saved draw.
              </div>
              <div>
                Matches are placed inside the season scheduling window (Start/End dates) and it tries to:
              </div>
              <div className="space-y-1 pl-4">
                <div>- Put home matches on each team’s preferred Home Day (if set).</div>
                <div>- If a Home Day slot isn’t possible, pick another weekday instead.</div>
                <div>- Avoid Irish public holidays.</div>
                <div>- Avoid scheduling matches between 15th December and 8th January.</div>
                <div>- Avoid any Blocked Dates added to the season (e.g. other competitions).</div>
                <div>- Spread matches out fairly evenly, with a slight bias to schedule more in the first half (to leave room for reschedules).</div>
              </div>
              <div>
                After fixtures are generated, the season moves from <span className="font-medium">draft</span> to <span className="font-medium">ready</span>.
              </div>
            </div>
          </div>
        </div>
      )}

      {cupDrawSeasonId && (() => {
        const cupDrawSeason = seasons.find((x) => x.id === cupDrawSeasonId);
        return (
          <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/30 px-4">
            <div className="w-full max-w-3xl max-h-[85vh] overflow-y-auto rounded-lg bg-white shadow-lg border p-5">
              <div className="flex items-start justify-between gap-4 mb-4">
                <div>
                  <div className="text-lg font-semibold text-gray-800">
                    Cup Draw: <span className="font-medium">{cupDrawSeason?.name || ''}</span>
                  </div>
                  <div className="text-sm text-gray-600">
                    Choose the first-round cup matches for each division: who plays who, and which side is home.
                    Saved draws are used when fixtures are generated.
                  </div>
                </div>
                <button className="btn btn-secondary" type="button" onClick={closeCupDraw}>
                  Close
                </button>
              </div>

              {cupDrawLoading ? (
                <div className="text-center py-6 text-gray-600">Loading cup draw...</div>
              ) : (
                <div className="space-y-4">
                  {cupDrawDivisions.map((d) => {
                    const slots = cupDrawSelections[d.division_id] || [];
                    const usedElsewhere = (slotIdx, side) => {
                      const taken = new Set();
                      slots.forEach((s, i) => {
                        for (const [sName, val] of [['home', s.home], ['away', s.away]]) {
                          if (!val) continue;
                          if (i === slotIdx && sName === side) continue;
                          taken.add(val);
                        }
                      });
                      return taken;
                    };
                    return (
                      <div key={d.division_id} className="border rounded-lg p-4">
                        <div className="flex flex-wrap justify-between items-center gap-2 mb-3">
                          <div className="font-medium text-gray-800">
                            {d.division_name}
                            {d.configured && !d.outdated && (
                              <span className="ml-2 text-xs font-normal text-green-700 bg-green-50 border border-green-200 rounded px-2 py-0.5">draw saved</span>
                            )}
                            {d.outdated && (
                              <span className="ml-2 text-xs font-normal text-red-700 bg-red-50 border border-red-200 rounded px-2 py-0.5">saved draw no longer matches division teams</span>
                            )}
                            {!d.configured && (
                              <span className="ml-2 text-xs font-normal text-gray-600 bg-gray-50 border border-gray-200 rounded px-2 py-0.5">not configured — random draw will be used</span>
                            )}
                          </div>
                          {(d.teams || []).length >= 2 && (
                            <div className="flex gap-2">
                              <button className="btn btn-secondary" type="button" onClick={() => randomizeCupDraw(d)}>
                                Randomize
                              </button>
                              <button
                                className="btn btn-success"
                                type="button"
                                onClick={() => submitCupDraw(d)}
                                disabled={cupDrawSaving === d.division_id}
                              >
                                {cupDrawSaving === d.division_id ? 'Saving...' : 'Save Draw'}
                              </button>
                              {d.configured && (
                                <button
                                  className="btn btn-danger"
                                  type="button"
                                  onClick={() => saveCupDraw(d, [])}
                                  disabled={cupDrawSaving === d.division_id}
                                >
                                  Clear
                                </button>
                              )}
                            </div>
                          )}
                        </div>

                        {(d.teams || []).length < 2 ? (
                          <div className="text-sm text-gray-500">Assign at least 2 teams to this division to set a cup draw.</div>
                        ) : (
                          <div className="space-y-2">
                            {slots.map((slot, i) => {
                              const takenHome = usedElsewhere(i, 'home');
                              const takenAway = usedElsewhere(i, 'away');
                              return (
                                <div key={i} className="flex flex-wrap items-center gap-2 text-sm">
                                  <span className="text-gray-500 w-16">Match {i + 1}</span>
                                  <select
                                    className="input flex-1 min-w-[10rem]"
                                    value={slot.home}
                                    onChange={(e) => setCupSlot(d.division_id, i, 'home', e.target.value)}
                                  >
                                    <option value="">— bye —</option>
                                    {(d.teams || []).map((t) => (
                                      <option key={t.id} value={t.id} disabled={takenHome.has(t.id)}>
                                        {t.name}
                                      </option>
                                    ))}
                                  </select>
                                  <span className="text-gray-500">vs</span>
                                  <select
                                    className="input flex-1 min-w-[10rem]"
                                    value={slot.away}
                                    onChange={(e) => setCupSlot(d.division_id, i, 'away', e.target.value)}
                                  >
                                    <option value="">— bye —</option>
                                    {(d.teams || []).map((t) => (
                                      <option key={t.id} value={t.id} disabled={takenAway.has(t.id)}>
                                        {t.name}
                                      </option>
                                    ))}
                                  </select>
                                </div>
                              );
                            })}
                            <div className="text-xs text-gray-500">
                              Left select is the home team; right is the away team. &quot;— bye —&quot; gives that team a
                              walkover into the next round.
                            </div>
                          </div>
                        )}
                      </div>
                    );
                  })}

                  {cupDrawDivisions.length === 0 && (
                    <div className="text-gray-500 text-center py-6">No divisions in this season yet.</div>
                  )}
                </div>
              )}
            </div>
          </div>
        );
      })()}

      {blockedDatesSeasonId && (() => {
        const blockedSeason = seasons.find((x) => x.id === blockedDatesSeasonId);
        const concluded = blockedSeason?.status === 'concluded';
        const generated = blockedSeason && blockedSeason.status !== 'draft';
        return (
          <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/30 px-4">
            <div className="w-full max-w-lg rounded-lg bg-white shadow-lg border p-5">
              <div className="flex items-start justify-between gap-4 mb-4">
                <div>
                  <div className="text-lg font-semibold text-gray-800">Blocked Dates</div>
                  <div className="text-sm text-gray-600">
                    {generated
                      ? 'Informational only — existing fixtures keep their dates; used for manual date changes.'
                      : 'No fixtures will be scheduled on these days for any club.'}
                    {' '}Add a name to mark the day as a tournament on the fixture calendar.
                  </div>
                </div>
                <button className="btn btn-secondary" type="button" onClick={closeBlockedDates}>
                  Close
                </button>
              </div>

              {!concluded && (
                <form onSubmit={addBlockedDate} className="mb-4 space-y-2">
                  <div className="flex gap-2">
                    <input
                      className="input flex-1"
                      type="date"
                      value={blockedDateInput}
                      onChange={(e) => setBlockedDateInput(e.target.value)}
                    />
                    <button className="btn btn-success" type="submit" disabled={!blockedDateInput}>
                      Add
                    </button>
                  </div>
                  <input
                    className="input w-full"
                    type="text"
                    maxLength={80}
                    placeholder="Tournament name (optional)"
                    value={blockedNameInput}
                    onChange={(e) => setBlockedNameInput(e.target.value)}
                  />
                </form>
              )}

              <div className="space-y-2 max-h-60 overflow-y-auto mb-4">
                {blockedDatesDraft.length === 0 && (
                  <div className="text-sm text-gray-500">No blocked dates selected.</div>
                )}
                {blockedDatesDraft.map((entry) => (
                  <div key={entry.date} className="flex justify-between items-center gap-2 border rounded p-2">
                    <span className="text-sm text-gray-800 whitespace-nowrap">{new Date(entry.date).toLocaleDateString()}</span>
                    {concluded ? (
                      <span className="flex-1 text-sm text-gray-600">{entry.name}</span>
                    ) : (
                      <input
                        className="input flex-1 !py-1 text-sm"
                        type="text"
                        maxLength={80}
                        placeholder="Tournament name"
                        value={entry.name}
                        onChange={(e) => updateBlockedDateName(entry.date, e.target.value)}
                      />
                    )}
                    {!concluded && (
                      <button
                        className="btn btn-danger text-xs"
                        type="button"
                        onClick={() => removeBlockedDate(entry.date)}
                      >
                        Remove
                      </button>
                    )}
                  </div>
                ))}
              </div>

              <div className="flex justify-end gap-2">
                <button className="btn btn-secondary" type="button" onClick={closeBlockedDates}>
                  {concluded ? 'Close' : 'Cancel'}
                </button>
                {!concluded && (
                  <button
                    className="btn btn-success"
                    type="button"
                    disabled={savingBlocked}
                    onClick={saveBlockedDates}
                  >
                    {savingBlocked ? 'Saving...' : 'Save'}
                  </button>
                )}
              </div>
            </div>
          </div>
        );
      })()}

      {fixturePreviewSeasonId && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/30 px-4">
          <div className="w-full max-w-2xl rounded-lg bg-white shadow-lg border p-5">
            <div className="flex items-start justify-between gap-4 mb-4">
              <div>
                <div className="text-lg font-semibold text-gray-800">Fixture Generation Preview</div>
                <div className="text-sm text-gray-600">Review what will be generated before confirming.</div>
              </div>
              <button className="btn btn-secondary" type="button" onClick={closeFixturePreview}>
                Close
              </button>
            </div>

            <div className="mb-4 text-sm text-gray-600">
              Choose a fixture multiplier per division below: 1 = 1 home + 1 away per pair,
              1.5 = 3 fixtures per pair (extra home/away random), 2 = 2 home + 2 away per pair.
            </div>

            {fixturePreviewLoading ? (
              <div className="text-gray-600">Loading preview...</div>
            ) : (
              <div className="space-y-4">
                <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
                  <div className="bg-gray-50 border rounded p-3">
                    <div className="text-xs text-gray-500">Start</div>
                    <div className="font-medium text-gray-800">
                      {fixturePreview?.schedule_start_date ? new Date(fixturePreview.schedule_start_date).toLocaleDateString() : '-'}
                    </div>
                  </div>
                  <div className="bg-gray-50 border rounded p-3">
                    <div className="text-xs text-gray-500">End</div>
                    <div className="font-medium text-gray-800">
                      {fixturePreview?.schedule_end_date ? new Date(fixturePreview.schedule_end_date).toLocaleDateString() : '-'}
                    </div>
                  </div>
                  <div className="bg-gray-50 border rounded p-3">
                    <div className="text-xs text-gray-500">Total fixtures</div>
                    <div className="font-medium text-gray-800">{fixturePreview?.total_fixtures ?? '-'}</div>
                  </div>
                  <div className="bg-gray-50 border rounded p-3">
                    <div className="text-xs text-gray-500">Cup fixtures</div>
                    <div className="font-medium text-gray-800">{fixturePreview?.total_cup_fixtures ?? '-'}</div>
                  </div>
                </div>

                <div>
                  <div className="text-sm font-medium text-gray-800 mb-2">Divisions</div>
                  <div className="overflow-x-auto">
                    <table className="table">
                      <thead>
                        <tr>
                          <th>Division</th>
                          <th>Teams</th>
                          <th>Multiplier</th>
                          <th>League</th>
                          <th>Cup</th>
                          <th>Total</th>
                        </tr>
                      </thead>
                      <tbody>
                        {(fixturePreview?.divisions || []).map((d) => (
                          <tr key={d.division_id}>
                            <td className="font-medium">{d.division_name}</td>
                            <td>{d.team_count}</td>
                            <td>
                              <select
                                className="input"
                                value={fixtureMultipliers[d.division_id] ?? d.fixture_multiplier ?? 1}
                                onChange={(e) => setDivisionMultiplier(d.division_id, Number(e.target.value))}
                              >
                                <option value={1}>1</option>
                                <option value={1.5}>1.5</option>
                                <option value={2}>2</option>
                              </select>
                            </td>
                            <td>{d.fixture_count}</td>
                            <td>{d.cup_fixture_count ?? 0}</td>
                            <td>{d.total_fixture_count ?? d.fixture_count}</td>
                          </tr>
                        ))}
                        {(!fixturePreview?.divisions || fixturePreview.divisions.length === 0) && (
                          <tr>
                            <td colSpan={6} className="text-gray-500">No divisions found.</td>
                          </tr>
                        )}
                      </tbody>
                    </table>
                  </div>
                </div>

                {(fixturePreview?.warnings || []).length > 0 && (
                  <div className="border rounded p-3 bg-yellow-50">
                    <div className="text-sm font-medium text-yellow-900 mb-2">Warnings</div>
                    <div className="space-y-1 text-sm text-yellow-900">
                      {(fixturePreview.warnings || []).map((w, idx) => (
                        <div key={idx}>- {w}</div>
                      ))}
                    </div>
                  </div>
                )}

                <div className="flex justify-end gap-2">
                  <button className="btn btn-secondary" type="button" onClick={closeFixturePreview}>
                    Cancel
                  </button>
                  <button
                    className="btn btn-warning"
                    type="button"
                    disabled={generating || !fixturePreview || (fixturePreview?.total_fixtures || 0) === 0}
                    onClick={async () => {
                      const regen = seasons.find((s) => s.id === fixturePreviewSeasonId)?.status === 'ready';
                      const msg = regen
                        ? 'Regenerate fixtures? This will DELETE all existing fixtures for this season and create a new schedule. Saved cup draws are kept.'
                        : 'Generate fixtures now? This will create the season schedule.';
                      if (!window.confirm(msg)) return;
                      await generateFixtures(fixturePreviewSeasonId);
                      closeFixturePreview();
                    }}
                  >
                    {generating ? 'Generating...' : 'Generate'}
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default Seasons;
